import { Button, makeStyles, Spinner } from "@fluentui/react-components";
import { AddFilled } from "@fluentui/react-icons";
import React from "react";
import { Link } from "react-router-dom";
import {
  FlightSelection,
  LayoutContext,
} from "../../../../../../context/LayoutContext";
import {
  $cloud100,
  $cloud40,
  $cloud60,
  $cloud70,
  $cloud80,
  $sky60,
  $sky90,
  $white,
} from "../../../../../../cssjs/colors";
import PageEnum from "../../../../../../enum/PageEnum";
import { useApiCall } from "../../../../../../hooks/useApiCall";
import { IFlightDetails } from "../../../../../../models/flights/IFlightsCommon";
import { getAirportCode } from "../../../../../../store/airport/AirportSelector";
import { useAppSelector } from "../../../../../../store/StoreHooks";
import { getApiUrl } from "../../../../../../utils/common/CommonHelperFunctions";
import { formatTime } from "../../../../../../utils/common/DateHelperFunctions";
import { getMappedDetails } from "../../../../../../utils/flights/FlightDetailHelperFunctions";
import FlightIconSelector from "../../../../../common/icon-selectors/FlightIconSelector";
import { AmdChevron } from "../../../../../common/icons/chevron/chevron";
import { ChevronDirection } from "../../../../../common/icons/chevron/chevronDirection";
import { NoDataCard } from "../../../../../common/no-data-card/NoDataCard";
import { FlightStatus } from "../FlightStatus";
import LiveTag from "./LiveTag";

export type MappedFlightDetail = {
  title: string;
  value: string | number | null;
};

const FlightDetailRows: React.FC<{
  details: MappedFlightDetail[];
  legType: any;
}> = ({ details, legType }) => {
  const classes = useStyles();
  const baseFields = new Set([
    "Status",
    "Terminal",
    "PAX",
    "Gate",
    "Altitude",
    "Latitude",
    "Longitude",
    "Speed",
    "Average flight time",
    "Heading direction",
    "Vertical rate of ascent",
    "Squawk",
  ]);

  const extraField = legType === "arrival" ? "Baggage belt" : "Baggage chute";
  const allowedFields = new Set([
    ...Array.from(baseFields),
    extraField,
    ...(legType === "departure" ? ["Check in counter"] : []),
  ]);

  return (
    <>
      {details
        .filter((detail) => allowedFields.has(detail.title))
        .map((detail) => (
          <div key={detail.title} className={classes.specRow}>
            <div className={classes.specTitleCell}>{detail.title}</div>
            <div>{detail.value}</div>
          </div>
        ))}
    </>
  );
};

const spaceBetween = "space-between";

const onCreateEventHandler = () => {
  window.sessionStorage.setItem("isNewEventClicked", "true");
};

export const FlightDetails: React.FC<FlightSelection> = ({
  setSelectedFlight,
  selectedFlight,
}) => {
  const { timezone } = React.useContext(LayoutContext);
  const airportCode = useAppSelector((state) => getAirportCode(state));

  const classes = useStyles();

  const handleClick = () => {
    setSelectedFlight({ flightDocID: undefined, flightID: undefined });
  };

  const getFlightDetailsUrl = getApiUrl(
    process.env.REACT_APP_API_FLIGHT_LIST_DETAIL,
    airportCode,
    new Date(),
    new Date(),
    timezone
  ).replace("{flightId}", selectedFlight?.flightDocID || "");

  const {
    data: flightDetailsData,
    error,
    isLoading,
  } = useApiCall<IFlightDetails>({
    url: getFlightDetailsUrl,
    dependencies: [airportCode, selectedFlight, timezone],
    requestType: "get",
  });

  const mappedDetails: MappedFlightDetail[] =
    getMappedDetails(flightDetailsData);
  const showDetails = mappedDetails?.length > 0;

  const {
    flightNumber,
    aircraftTypeName,
    legType,
    liveTracked,
    callSign,
    progressPercentage,
    trafficType,
    origin,
    destination,
    aobt,
    sobt,
    eobt,
    sibt,
    aibt,
    eibt,
  } = flightDetailsData || {};

  const trafficText = trafficType === "International" ? "INTL" : "DOM";
  const actualTimeDeparture = formatTime(aobt || eobt || sobt);
  const actualTimeArrival = formatTime(aibt || eibt || sibt);
  const scheduleTimeDeparture = formatTime(sobt);
  const scheduleTimeArrival = formatTime(sibt);

  //Temporary checks for YQB flight details
  const isYQB = airportCode === "YQB";

  return (
    <div className={classes.card} data-testid="flight-details-card">
      <div className={classes.cardHeaderContainer}>
        <div className={classes.cardHeader}>
          <div className={classes.backButton} onClick={handleClick}>
            <AmdChevron direction={ChevronDirection.RIGHT} />
          </div>
          {!isLoading && (
            <>
              <p className={classes.buttonText}>{flightNumber}</p>
              {flightNumber && <FlightIconSelector legType={legType} />}
              <div className={classes.liveTag}>
                {liveTracked && <LiveTag liveTracked={liveTracked} />}
              </div>
            </>
          )}
        </div>
        {!isYQB && (
          <Link to={`/${PageEnum.PageUrl.EventManagement}`}>
            <Button
              appearance="outline"
              onClick={onCreateEventHandler}
              icon={<AddFilled className="mr-1" />}
              className={`${classes.eventBtn}`}
            >
              New Event
            </Button>
          </Link>
        )}
      </div>
      {error && <NoDataCard message="Flight ID not available." />}
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          style={{ height: "35vh" }}
        />
      )}
      {!error && !isLoading && !showDetails && (
        <NoDataCard message="No Flight Details data found." />
      )}
      {!error && !isLoading && showDetails && (
        <>
          <section className={classes.infoSection}>
            <div className={classes.infoDetails}>
              {aircraftTypeName && <div>{aircraftTypeName}</div>}
              {flightNumber && <div>{flightNumber}</div>}
              {trafficText && <div>{trafficText}</div>}
              {callSign && <div>CALLSIGN: {callSign}</div>}
            </div>
            <div className={classes.flightStatus}>
              <FlightStatus
                origin={origin}
                destination={destination}
                progressPercentage={progressPercentage}
                isDetails={true}
              />
            </div>
            <div className="amd-container-hspacebtwn fs-12">
              <div>
                <span className={classes.timeDisplayText}>STD </span>
                {scheduleTimeDeparture}
              </div>
              <div>
                <span className={classes.timeDisplayText}>STA </span>
                {scheduleTimeArrival}
              </div>
            </div>

            <div className="amd-container-hspacebtwn fs-12">
              <div>
                <span className={classes.timeDisplayText}>ATD </span>
                {actualTimeDeparture}
              </div>
              <div>
                <span className={classes.timeDisplayText}>ATA </span>
                {actualTimeArrival}
              </div>
            </div>
          </section>

          <section className={classes.specificationSection}>
            <div className={classes.specTitle}>Flight Details</div>
            <div className={classes.specPanel}>
              <div className={classes.panelContent}>
                <FlightDetailRows details={mappedDetails} legType={legType} />
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  card: {
    padding: "16px",
    background: $white,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  cardHeaderContainer: {
    display: "flex",
    justifyContent: spaceBetween,
    alignItems: "center",
    marginBottom: "18px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    padding: "8px 0px",
  },
  backButton: {
    cursor: "pointer",
    maxHeight: "14px",
  },
  buttonText: {
    padding: "0px 10px",
    margin: "0",
    fontWeight: 500,
    fontSize: "16px",
    color: $sky90,
  },
  infoSection: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "432px",
    marginBottom: "8px",
  },
  infoDetails: {
    display: "flex",
    justifyContent: spaceBetween,
    fontSize: "12px",
    color: $cloud70,
    paddingBottom: "8px",
  },
  flightStatus: {
    marginBottom: "4px",
  },
  timeDisplayText: {
    color: $cloud60,
  },
  specificationSection: {
    fontSize: "14px",
    color: $cloud80,
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
  specTitle: {
    fontWeight: 700,
    display: "flex",
    width: "100%",
    justifyContent: "space-around",
    borderBottom: `1px solid ${$cloud40}`,
    paddingBottom: "8px",
  },
  specPanel: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  panelContent: {
    overflowY: "auto",
  },
  specRow: {
    display: "flex",
    padding: "8px 4px",
    borderBottom: `1px solid ${$cloud40}`,
    fontSize: "14px",
    color: $cloud100,
    fontWeight: 500,
  },
  specTitleCell: {
    flexBasis: "50%",
    color: $cloud80,
  },
  liveTag: {
    marginTop: "3px",
  },
  eventBtn: {
    height: "30px",
    border: `1px solid ${$sky60}`,
    color: $sky60,
    "&:hover": {
      color: $cloud80,
    },
  },
});
